<template>
  <div>
    <v-row density="compact">
      <v-col
        cols="12"
        sm="6"
        md="4"
        class="d-flex flex-column"
      >
        <!-- Publishers Card -->
        <global-card
          :with-actions="false"
          :title="t('user.card')"
          class="text-center mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <user-profile-image
            class="mt-2"
            :user="user"
            update-route="/api/users/{user.id}/profileimage"
            @status-change="onProfileImageStatusChange"
            @status-error="onProfileImageStatusError"
          />
          <v-card-text>
            <span class="text-headline-small text-primary-darken-1">
              {{ user.full_name }}
            </span>
            <br>
            <span
              v-if="user.congregation"
              class="text-primary text-body-large"
            >
              {{ user.congregation.name }}
            </span>
            <br>
            <span
              v-if="user.circuit"
              class="text-primary text-body-large"
            >
              {{ user.circuit.name }}
            </span>
            <br>
            <phone-number
              v-if="user.mobile_phone"
              class="mb-2"
              :number="user.mobile_phone"
            />
            <phone-number
              v-if="user.landline_phone"
              :number="user.landline_phone"
              icon="fas fa-phone"
              class="mb-2"
            />

            <span v-if="!user.landline_phone && !user.mobile_phone">-</span>
            <br>
            <a
              variant="plain"
              class="text-primary-darken-1 text-lowercase text-decoration-none text-body-large"
              :href="`mailto:${user.email}`"
            >
              <v-icon
                color="primary-darken-1"
                icon="fas fa-envelope"
                class="me-1"
              />
              {{ user.email }}
            </a>
          </v-card-text>
        </global-card>
        <!-- Personal Details -->
        <global-card
          :with-actions="false"
          :title="t('user.personal_information')"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <v-card-text>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('user.year_of_birth') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.year_of_birth ? user.year_of_birth : '-' }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('user.date_of_baptism') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{
                user.date_of_baptism
                  ? toLongDate(user.date_of_baptism)
                  : '-'
              }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('email.title') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.email }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('language.title', 2) }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.languages_descr ? user.languages_descr : '-' }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('language.preferred_language') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.preferred_language ?? '-' }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('global.spouse') }}
            </div>
            <div
              v-if="spouseDetailsUrl && user.spouse"
              class="mb-1 ms-3 text-body-large text-primary-darken-1"
            >
              <router-link :to="spouseDetailsUrl">
                <u>{{ user.spouse.full_name }}</u>
              </router-link>
            </div>
            <div
              v-else
              class="mb-1 ms-3 text-body-large"
            >
              -
            </div>
          </v-card-text>
        </global-card>
        <!-- Emergency Contact Details -->
        <global-card
          :with-actions="false"
          :title="t('user.emergency_contact')"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <v-card-text>
            <div class="text-body-large text-primary-darken-1">
              {{ t('user.emergency_contact') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{
                user.emergency_contact_name ? user.emergency_contact_name : '-'
              }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('global.phone') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              <phone-number
                v-if="user.emergency_contact_phone"
                :number="user.emergency_contact_phone"
              />
              <span v-else>-</span>
            </div>
          </v-card-text>
        </global-card>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="4"
        class="d-flex flex-column"
      >
        <!-- Congregation -->
        <global-card
          :with-actions="false"
          :title="t('congregation.title')"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <v-card-text>
            <div v-if="user.congregation">
              <div class="text-body-large text-primary-darken-1">
                {{ t('congregation.name') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.congregation.name }}
              </div>
              <div class="text-body-large text-primary-darken-1">
                {{ t('congregation.within_city_limits') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.congregation.within_city_limits ? t('global.yes') : t('global.no') }}
              </div>
            </div>
            <div v-if="user.circuit">
              <div class="text-body-large text-primary-darken-1">
                {{ t('circuit.name') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.circuit.name }}
              </div>
            </div>
            <div class="text-body-large text-primary-darken-1">
              {{ t('smpw.appointed') }}
            </div>
            <div
              v-if="user.appointed"
              class="mb-1 ms-3 text-body-large"
            >
              {{ t(`appointment.${user.appointed}`) }}
            </div>
            <div
              v-else
              class="mb-1 ms-3 text-body-large"
            >
              {{ t('global.not_appointed') }}
            </div>
            <div class="text-body-large text-primary-darken-1">
              {{ t('user.publisher_type') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ t(`user.types.${user.type}`) }}
            </div>
          </v-card-text>
        </global-card>
        <!-- Account -->
        <global-card
          :with-actions="false"
          :title="t('user.account')"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <v-card-text>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('tags.title', 2) }}
            </div>
            <v-chip
              v-for="tag in user.tags"
              :key="tag"
              class="mr-1 mb-1"
              :text="tag"
            />
            <div v-if="!user.tags?.length">
              <span class="text-body-large">-</span>
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('global.username') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.username }}
            </div>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('user.temporary') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.temporary_publisher ? t('global.yes') : t('global.no') }}
            </div>

            <div v-if="showExtendedInfo">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('user.qualified_trainer') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.qualified_trainer ? t('global.yes') : t('global.no') }}
              </div>
            </div>

            <div v-if="showExtendedInfo && user.can_be_captain">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('global.qualifies_as_captain', { captain: getSetting('captain_label') }) }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.qualified_captain ? t('global.yes') : t('global.no') }}
              </div>
            </div>

            <div v-if="showExtendedInfo">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('user.has_stand') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.has_stand ? t('global.yes') : t('global.no') }}
              </div>
            </div>

            <div v-if="showExtendedInfo">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('user.temperature_sensitivity') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.temperature_sensitivity ? t('global.yes') : t('global.no') }}
              </div>
            </div>

            <div v-if="user.roles_descr">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('role.access') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.roles_descr }}
              </div>
            </div>

            <div v-if="user.teams_descr">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('team.title', 2) }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.teams_descr }}
              </div>
            </div>

            <div v-if="user.permissions_descr">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('permission.access') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.permissions_descr }}
              </div>
            </div>

            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('shift.cancellation.rate') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ (parseFloat(user.calc_cancellation_rate) * 100).toFixed(2) }} %
            </div>

            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('shift.cancellation.last_minute_rate') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ (parseFloat(user.calc_last_minute_cancellation_rate) * 100).toFixed(2) }} %
            </div>

            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('login.last') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{
                user.last_login
                  ? toLongDateWithTime(user.last_login)
                  : t('date.never')
              }}
            </div>

            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('shift.last') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{
                user.latest_assigned_shift_date
                  ? toLongDate(user.latest_assigned_shift_date)
                  : t('date.never')
              }}
            </div>

            <div v-if="showExtendedInfo">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('date.last_accessed') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{
                  user.last_access
                    ? toLongDateWithTime(user.last_access)
                    : t('date.never')
                }}
              </div>
            </div>
          </v-card-text>
        </global-card>
      </v-col>

      <v-col
        cols="12"
        sm="6"
        md="4"
        class="d-flex flex-column"
      >
        <!-- Shift Preferences -->
        <global-card
          :with-actions="false"
          :title="t('user.shift_preferences')"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <v-card-text>
            <div class="mb-1 text-body-large text-primary-darken-1">
              {{ t('shift.per_week') }}
            </div>
            <div class="mb-1 ms-3 text-body-large">
              {{ user.shifts_per_week }}
            </div>
            <div
              v-if="user.events?.length"
              class="mt-4"
            >
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('event.title', 2) }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                <ul>
                  <li
                    v-for="event in user.events"
                    :key="event.id"
                  >
                    {{ event.name }}
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="getSetting('allow_location_selection')">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('location.preferred') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                <ul>
                  <li
                    v-for="location in user.prefered_locations"
                    :key="location.id"
                  >
                    {{ location.name }}
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="getSetting('send_daily_shift_reminders') && user.send_daily_shift_reminders !== undefined">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('setting.send_daily_shift_reminders') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.send_daily_shift_reminders ? t('states.enabled') : t('states.disabled') }}
              </div>
            </div>
            <div v-if="getSetting('send_weekly_shift_reminders') && user.send_weekly_shift_reminders !== undefined">
              <div class="mb-1 text-body-large text-primary-darken-1">
                {{ t('setting.send_weekly_shift_reminders') }}
              </div>
              <div class="mb-1 ms-3 text-body-large">
                {{ user.send_weekly_shift_reminders ? t('states.enabled') : t('states.disabled') }}
              </div>
            </div>
          </v-card-text>
        </global-card>

        <!-- All Training Sessions -->
        <global-card
          :with-actions="false"
          :title="t('course.training_sessions')"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
        >
          <v-card-text>
            <!-- Completed Trainings -->

            <v-alert
              v-if="completedTraining.length === 0"
              :text="t('user.status.no_completed_training')"
              class="text-font"
            >
              <template #title>
                <span class="text-title-medium">
                  {{ t('states.completed') }}
                </span>
              </template>
            </v-alert>

            <v-alert
              v-for="(courseSession, courseIndex) in completedTraining"
              :key="courseIndex"
              type="success"
              icon="fa-solid fa-square-check"
              class="my-1"
              :title="courseSession.course?.name"
            >
              {{ t('date.completed_on', { date: toLongDate(courseSession.date) }) }}
              <v-btn
                v-if="showExtendedInfo && can('manage_course_sessions')"
                v-tooltip="t('course.delete_training')"
                color="error"
                class="ma-2"
                @click="showUninviteModal(courseSession)"
              >
                <v-icon icon="fas fa-trash" />
              </v-btn>
            </v-alert>

            <!-- Pending Trainings -->

            <v-alert
              v-if="invitedTraining.length === 0"
              :text="t('course.no_invites')"
              class="my-1 text-body-large"
            >
              <template #title>
                <span class="text-title-medium">
                  {{ t('course.invitations') }}
                </span>
              </template>
            </v-alert>

            <v-alert
              v-for="(courseSession, courseIndex) in invitedTraining"
              :key="courseIndex"
              type="warning"
              density="compact"
              class="my-1 text-body-large"
              :title="courseSession.course?.name"
              icon="fas fa-clock"
            >
              {{ t('date.invited_for', { date: toLongDate(courseSession.date) }) }}
              <v-btn
                v-if="can('manage_course_sessions')"
                v-tooltip="t('course.uninvite')"
                color="error"
                class="ma-2"
                @click="showUninviteModal(courseSession)"
              >
                <v-icon icon="fas fa-trash" />
              </v-btn>
              <template #title>
                <span class="text-title-medium text-warning">
                  {{ courseSession.course?.name }}
                </span>
              </template>
            </v-alert>

            <!-- Declined Trainings -->

            <v-alert
              v-if="declinedTraining.length === 0"
              :text="t('course.no_declined_training')"
              class="my-1 text-body-large"
            >
              <template #title>
                <span class="text-title-medium">
                  {{ t('course.declined') }}
                </span>
              </template>
            </v-alert>

            <v-alert
              v-for="(courseSession, courseIndex) in declinedTraining"
              :key="courseIndex"
              type="error"
              class="my-1"
              icon="fa-solid fa-square-xmark"
              :title="courseSession.course?.name"
            >
              {{ t('course.declined') }}:
              {{ toLongDate(courseSession.date) }}
              <v-btn
                v-if="can('manage_course_sessions')"
                v-tooltip="t('course.remove')"
                color="error"
                class="ma-2"
                @click="showUninviteModal(courseSession)"
              >
                <v-icon icon="fas fa-trash" />
              </v-btn>
            </v-alert>
          </v-card-text>
        </global-card>
        <!-- Approvals -->
        <global-card
          v-if="shouldDisplayQualifications"
          :with-actions="false"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
          :title="t('global.approvals')"
        >
          <v-card-text>
            <div
              v-if="user.qualified_captain"
              class="mb-1"
            >
              <v-btn
                color="primary"
                class="me-3 hover_off w-25"
                size="default"
                variant="flat"
                :ripple="false"
              >
                <v-icon
                  size="large"
                  :icon="user.gender === 'b' ? 'fas fa-user-tie' : 'fas-fa-user'"
                />
              </v-btn>
              <span class="text-font">
                {{ t('global.approved_as', { title: getSetting('captain_label') }) }}
              </span>
            </div>
            <div
              v-if="user.qualified_trainer"
              class="mb-1"
            >
              <v-btn
                color="success"
                size="default"
                variant="flat"
                :ripple="false"
                class="me-3 hover_off w-25"
              >
                <v-icon
                  icon="fas fa-chalkboard-teacher"
                  size="large"
                />
              </v-btn>
              {{ t('course.approved_trainer') }}
            </div>
          </v-card-text>
        </global-card>
        <global-card
          :with-actions="false"
          class="mb-3"
          :class="{ 'mr-3': smAndUp }"
          :title="t('user.linked_publishers')"
        >
          <v-card-text>
            <div
              v-if="user.requires_linked_user_for_shift"
              class="text-body-large text-error text-primary-darken-1"
            >
              {{ t('user.required_shift_guardian') }}
            </div>

            <div
              v-if="user.child_users?.length"
              class="text-body-large text-primary-darken-1"
            >
              {{ t('user.child_users', { name: user.name }) }}
            </div>
            <div
              v-for="child in user.child_users"
              :key="child.id"
              class="text-body-large"
            >
              {{ child.name }} - {{ t(`user.relationships.${child.pivot.relationship}`) }}<br>
            </div>
            <div
              v-if="user.parent_users?.length"
              class="text-body-large text-primary-darken-1"
            >
              {{ t('user.parent_users', { name: user.name }) }}
            </div>
            <div
              v-for="parent in user.parent_users"
              :key="parent.id"
              class="text-body-large"
            >
              {{ parent.name }} - {{ t(`user.relationships_inverse.${parent.pivot.relationship}`) }}<br>
            </div>
            <v-alert
              v-if="user.parent_users?.length === 0 && user.child_users?.length === 0"
              class="text-font text-body-large"
              :text="t('user.no_linked_publishers')"
            />
          </v-card-text>
        </global-card>
      </v-col>
    </v-row>
    <v-dialog
      v-model="dialog"
      max-width="500px"
    >
      <global-card
        :with-actions="true"
        :title="t('course.uninvite')"
        closable
        @close="dialog = false"
      >
        {{ t('course.uninvite_confirmation_long', { name: user.full_name, course: selectedCourseSession?.course?.name }) }}
        <template #actions>
          <v-btn
            color="dark-grey"
            :text="t('global.no')"
            @click="dialog = false"
          />
          <v-btn
            color="primary"
            :text="t('global.yes')"
            @click="uninviteFromCourseSession"
          />
        </template>
      </global-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import {
  useAuth,
  useDate,
  usePermissions,
  useSettings,
} from '@/composables';
import { useErrorStore } from '@/stores/errors';
import UserProfileImage from './UserProfileImage.vue';

const props = withDefaults(defineProps<{
  user: App.Models.User;
  showExtendedInfo?: boolean;
}>(), {
  showExtendedInfo: () => false,
});

const emit = defineEmits(['refresh']);

const { t } = useI18n();
const { smAndUp } = useDisplay();
const { authUser } = useAuth();
const { getSetting } = useSettings();
const { can } = usePermissions();
const { toLongDate, toLongDateWithTime } = useDate();
const { showSnackMessage, handleError } = useErrorStore();

const dialog = ref(false);
const selectedCourseSession = ref<App.Models.CourseSession>();
const shouldDisplayQualifications = computed(() => props.user.qualified_captain || props.user.qualified_trainer);

const spouseDetailsUrl = computed(() => {
  if (!props.user.spouse) {
    return undefined;
  }
  if (props.showExtendedInfo) {
    return `/admin/users/${props.user.spouse.id}`;
  }
  if (props.user.id === authUser.id) {
    return '/myspousedetails';
  }
  if (props.user.id === authUser.spouse_id) {
    return '/mydetails';
  }
  return undefined;
});

const completedTraining = computed(() => {
  if (props.user.course_sessions) {
    return props.user.course_sessions.filter((s) => (s.pivot.status === 'attended'));
  }
  return [];
});

const invitedTraining = computed(() => {
  if (props.user.course_sessions) {
    return props.user.course_sessions.filter((s) => (s.pivot.status === 'pending') || (s.pivot.status === 'accepted'));
  }
  return [];
});

const declinedTraining = computed(() => {
  if (props.user.course_sessions) {
    return props.user.course_sessions.filter((s) => (s.pivot.status === 'declined'));
  }
  return [];
});

async function onProfileImageStatusChange(approved: boolean): Promise<void> {
  const newStatus = approved
    ? t('states.approved')
    : t('states.rejected');
  showSnackMessage(t('images.profile_status', { status: newStatus }), 'success');
  emit('refresh');
}

async function onProfileImageStatusError(approved: boolean): Promise<void> {
  showSnackMessage(approved
    ? t('images.cannot_approve')
    : t('images.cannot_reject'));
}

function showUninviteModal(courseSession: App.Models.CourseSession): void {
  selectedCourseSession.value = courseSession;
  dialog.value = true;
}

async function uninviteFromCourseSession() {
  if (!selectedCourseSession.value?.id) {
    return;
  }
  try {
    await axios.delete(`/api/coursesessions/${selectedCourseSession.value.id}/uninvite`, {
      params: { user_id: props.user.id },
    });
    emit('refresh');
    dialog.value = false;
    selectedCourseSession.value = undefined;
  }
  catch (error) {
    handleError(error, t('course.cannot_uninvite', { name: props.user.full_name }));
  }
}
</script>
